'use client';

import { FC, useState, useMemo, useEffect } from 'react';
import { Box, Button, Divider, IconButton, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ScienceIcon from '@mui/icons-material/Science';
import InventoryIcon from '@mui/icons-material/Inventory';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormAutocomplete, FormDatePicker, FormTextField } from '@/components/common/FormComponents';
import { OptionKey, KeyValueType } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useDialogStore } from '@/stores/dialogStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { useBackendStore } from '@/stores/backendStore';
import { useAuthStore } from '@/stores/authStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import {
  type CreateMultiSampleGrowthRequest,
  type CreateMultiSampleGrowthSubstrateDto,
  type ProjectDto,
  PrivilegeEnum,
  type ReportingCategoryDto,
  type SampleDto,
  RecipeFavoriteDto,
  RecipeTemplateStep,
} from '@/api/ApiClient';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { IdLabelPair } from '@/types/common';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { uniqueSortedDropdownStrings } from '@/lib/utils/substrateLookup';
import {
  createProjectResolver,
  sampleProjectMultiplicityFieldHint,
  validateProjectIdsForSampleMultiplicity,
} from '@/lib/utils/sampleProjectMultiplicity';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import { buildCreateRecipePayloadAsync, fetchRecipeFavoritesForProcessing } from '@/lib/utils/recipeFavoriteAtCreate';
import { log } from '@/lib/utils/logger';

const OPTION_KEYS: OptionKey[] = [
  'projects',
  'projectType',
  'substrateDropdownChoices',
  'materials',
  'synthesisTechniquesLeafOnly',
  'synthesisInstruments',
  'synthesisInstrumentOptions',
  'researcherOptions',
  'sampleReportingCategory',
  'sampleDropdownChoices',
];

interface MultiSampleGrowthDialogProps {
  open: boolean;
  onClose: () => void;
  project?: ProjectDto | undefined;
  onSubmit: (growthRun: SampleDto) => void;
}

interface SubstrateRowState {
  id: number;
  mode: 'existing' | 'new';
  existingSample: SampleDto | null;
  /** For mode 'new': substrate fields matching AddToInventory */
  materialIds: number[];
  vendor: string;
  lot: string;
  orientation: string;
  holder: string;
  thickness: string;
  quality: string;
  size: string;
}

export const MultiSampleGrowthDialog: FC<MultiSampleGrowthDialogProps> = ({ open, onClose, project, onSubmit }) => {
  const backend = useBackendStore((s) => s.getBackend());
  const systemConfig = useSystemConfig();
  const multiplicityPolicy = systemConfig?.sampleProjectMultiplicity;
  const multiplicityHint = sampleProjectMultiplicityFieldHint(multiplicityPolicy);
  const currentUser = useAuthStore((s) => s.currentUser);
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const allowCreateWithoutProject = canCreateWithoutProject(isAuthorized);
  const hasCreateSampleForOthers = isAuthorized(PrivilegeEnum.CreateSampleForOthers, 'read');
  const { options } = useSelectOptions(OPTION_KEYS);
  const { showSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const { showProjectSelectionDialog, showSampleSelectionDialog } = useDialogStore();
  const { hasUnsavedChanges, markAsInteracted, reset } = useUserInteraction();

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedProject, setSelectedProject] = useState<{ id: number; label: string } | null>(
    project?.id ? { id: project.id!, label: project.projectLabel! } : null
  );
  const [selectedStaff, setSelectedStaff] = useState<KeyValueType | null>(null);

  const defaultSelectedStaff = useMemo(() => {
    if (!options.researcherOptions?.length) return null;
    const match =
      currentUser?.id != null
        ? options.researcherOptions.find((opt) => String(opt.key) === String(currentUser.id))
        : null;
    return match ?? null;
  }, [currentUser, options.researcherOptions]);

  const effectiveStaff = selectedStaff ?? defaultSelectedStaff;

  const [selectedTechnique, setSelectedTechnique] = useState<{ id: string; name: string } | null>(null);
  const [selectedInstrument, setSelectedInstrument] = useState<{ id: string; name: string } | null>(null);
  const [selectedReportingCategory, setSelectedReportingCategory] = useState<ReportingCategoryDto | null>(null);
  const [growthLabel, setGrowthLabel] = useState('');
  const [description, setDescription] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState<string[]>([]);
  const [selectedRecipeTemplate, setSelectedRecipeTemplate] = useState<string | null>(null);
  const [availableFavorites, setAvailableFavorites] = useState<RecipeFavoriteDto[]>([]);
  const [selectedFavorite, setSelectedFavorite] = useState<RecipeFavoriteDto | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);

  const createEmptyExistingRow = (): SubstrateRowState => ({
    id: 0,
    mode: 'existing',
    existingSample: null,
    materialIds: [],
    vendor: '',
    lot: '',
    orientation: '',
    holder: '',
    thickness: '',
    quality: '',
    size: '',
  });

  const [substrateRows, setSubstrateRows] = useState<SubstrateRowState[]>(() => {
    const r = createEmptyExistingRow();
    r.id = 1;
    return [r];
  });

  const [staffError, setStaffError] = useState(false);
  const [projectError, setProjectError] = useState(false);
  const [techniqueError, setTechniqueError] = useState(false);
  const [instrumentError, setInstrumentError] = useState(false);
  const [substrateError, setSubstrateError] = useState(false);

  // When user cannot create samples for others, restrict researcher to current user only.
  const staffOptions = useMemo(() => {
    const list = options.researcherOptions ?? [];
    if (hasCreateSampleForOthers) return list;
    if (!currentUser?.id) return [];
    const match = list.find((r) => String(r.key) === String(currentUser.id));
    return match ? [match] : [];
  }, [options.researcherOptions, hasCreateSampleForOthers, currentUser]);

  const currentUserNotInStaffList =
    !hasCreateSampleForOthers && currentUser?.id != null && options.researcherOptions?.length !== undefined
      ? !options.researcherOptions.some((r) => String(r.key) === String(currentUser.id))
      : false;

  const projectOptions = useMemo(
    () =>
      Array.from(
        new Map(
          options.projects
            ?.filter((p) => p.id !== undefined && p.projectLabel !== undefined)
            .map((p) => [p.id!, { id: p.id!, label: p.projectLabel! }])
        ).values()
      ),
    [options.projects]
  );

  const resolveProjectDto = useMemo(
    () => createProjectResolver(options.projects, options.projectType, project),
    [options.projects, options.projectType, project]
  );

  /** Leaf-only techniques (top-level with no children + all children). From options. */
  const techniqueOptions = useMemo(
    () =>
      (options.synthesisTechniquesLeafOnly ?? [])
        .filter((t) => t.id && t.name)
        .map((t) => ({ id: t.id!, name: t.name! })),
    [options.synthesisTechniquesLeafOnly]
  );

  const instrumentOptions = useMemo(() => {
    if (!selectedTechnique?.id) return [];
    const base = options.synthesisInstruments ?? [];
    const formatted = options.synthesisInstrumentOptions ?? [];
    const filteredIds = new Set(
      base.filter((inst) => inst.synthesisTechniques?.includes(selectedTechnique.id)).map((inst) => inst.id)
    );
    return formatted
      .filter((opt) => filteredIds.has(opt.key))
      .map((opt) => ({
        id: opt.key,
        name: opt.value || opt.key,
      }));
  }, [options.synthesisInstruments, options.synthesisInstrumentOptions, selectedTechnique]);

  const reportingCategoryOptions = useMemo(
    () => options.sampleReportingCategory ?? [],
    [options.sampleReportingCategory]
  );

  const synStep: RecipeTemplateStep | null = toRecipeTemplateStep('SYN') ?? null;

  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();
    const loadTemplates = async () => {
      if (!selectedTechnique?.id || !selectedInstrument?.id || !synStep) {
        if (!controller.signal.aborted) {
          setAvailableTemplates([]);
          setSelectedRecipeTemplate(null);
          setAvailableFavorites([]);
          setSelectedFavorite(null);
        }
        return;
      }

      setLoadingTemplates(true);
      try {
        const templates = await backend.recipeTemplate.availableTemplates(
          synStep,
          selectedTechnique.id,
          selectedInstrument.id
        );
        if (controller.signal.aborted) {
          return;
        }
        const names = templates.map((t) => t.name).filter((name): name is string => !!name);
        setAvailableTemplates(names);
        setSelectedRecipeTemplate(names.length > 0 ? names[0] : null);
        setSelectedFavorite(null);
      } catch (err) {
        if (!controller.signal.aborted) {
          log.error('[MultiSampleGrowthDialog] Failed to load recipe templates:', err);
          setAvailableTemplates([]);
          setSelectedRecipeTemplate(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingTemplates(false);
        }
      }
    };
    void loadTemplates();
    return () => controller.abort();
  }, [open, backend, selectedTechnique?.id, selectedInstrument?.id, synStep]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();
    const loadFavorites = async () => {
      if (!selectedRecipeTemplate || !synStep || !selectedTechnique?.id || !selectedInstrument?.id) {
        if (!controller.signal.aborted) {
          setAvailableFavorites([]);
        }
        return;
      }

      setLoadingFavorites(true);
      try {
        const favorites = await fetchRecipeFavoritesForProcessing(
          backend,
          selectedTechnique.id,
          selectedInstrument.id,
          selectedRecipeTemplate,
          'SYN'
        );
        if (!controller.signal.aborted) {
          setAvailableFavorites(favorites);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          log.error('[MultiSampleGrowthDialog] Failed to load recipe favorites:', err);
          setAvailableFavorites([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingFavorites(false);
        }
      }
    };
    void loadFavorites();
    return () => controller.abort();
  }, [open, backend, synStep, selectedTechnique?.id, selectedInstrument?.id, selectedRecipeTemplate]);

  const sd = options.substrateDropdownChoices;

  const substrateVendorOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.vendors), [sd?.vendors]);

  const substrateLotOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.lots), [sd?.lots]);

  const substrateOrientationOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.orientations), [sd?.orientations]);

  /** Materials deduplicated by id so Autocomplete never sees duplicate option ids. */
  const materialOptions = useMemo(() => {
    const list =
      options.materials
        ?.filter((m) => m.id != null && m.name && m.name !== 'None')
        .map((m) => ({ id: m.id!, label: m.name! })) ?? [];
    const byId = new Map<number, { id: number; label: string }>();
    for (const m of list) {
      if (!byId.has(m.id)) byId.set(m.id, m);
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [options.materials]);

  const handleProjectLookup = async () => {
    const projects = await showProjectSelectionDialog('Select Project', { allowMultiple: false });
    if (projects && projects.length > 0) {
      const p = projects[0];
      setSelectedProject({ id: p.id!, label: p.projectLabel! });
      markAsInteracted();
    }
  };

  const handleAddRow = () => {
    setSubstrateRows((rows) => {
      const r = createEmptyExistingRow();
      r.id = rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
      return [...rows, r];
    });
    markAsInteracted();
  };

  const handleRemoveRow = (id: number) => {
    setSubstrateRows((rows) => rows.filter((r) => r.id !== id));
    markAsInteracted();
  };

  const handleRowModeChange = (id: number, mode: 'existing' | 'new') => {
    setSubstrateRows((rows) =>
      rows.map((r) =>
        r.id === id
          ? {
              ...r,
              mode,
              existingSample: mode === 'existing' ? r.existingSample : null,
              ...(mode === 'new'
                ? {
                    materialIds: [],
                    vendor: '',
                    lot: '',
                    orientation: '',
                    holder: '',
                    thickness: '',
                    quality: '',
                    size: '',
                  }
                : {}),
            }
          : r
      )
    );
    markAsInteracted();
  };

  const handleRowPickSample = async (id: number) => {
    // One substrate per row: open the shared sample picker in single-select mode (no checkboxes).
    const samples = await showSampleSelectionDialog('Select substrate sample', undefined, undefined, {
      allowMultiple: false,
    });
    if (samples && samples.length > 0) {
      const sample = samples[0];
      setSubstrateRows((rows) => rows.map((r) => (r.id === id ? { ...r, existingSample: sample } : r)));
      markAsInteracted();
    }
  };

  const handleRowNewSubstrateChange = <K extends keyof SubstrateRowState>(
    id: number,
    field: K,
    value: SubstrateRowState[K]
  ) => {
    setSubstrateRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    markAsInteracted();
  };

  const resetStateAndClose = () => {
    setSelectedDate(new Date());
    setSelectedProject(project?.id ? { id: project.id!, label: project.projectLabel! } : null);
    const staffMatch =
      currentUser?.id != null && options.researcherOptions?.length
        ? options.researcherOptions.find((opt) => String(opt.key) === String(currentUser.id))
        : null;
    setSelectedStaff(staffMatch ?? null);
    setSelectedTechnique(null);
    setSelectedInstrument(null);
    setSelectedReportingCategory(null);
    setGrowthLabel('');
    setDescription('');
    setAvailableTemplates([]);
    setSelectedRecipeTemplate(null);
    setAvailableFavorites([]);
    setSelectedFavorite(null);
    const r = createEmptyExistingRow();
    r.id = 1;
    setSubstrateRows([r]);
    setStaffError(false);
    setProjectError(false);
    setTechniqueError(false);
    setInstrumentError(false);
    setSubstrateError(false);
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (!effectiveStaff?.key) {
      setStaffError(true);
      hasError = true;
    } else if (!hasCreateSampleForOthers && String(effectiveStaff.key) !== String(currentUser?.id)) {
      setStaffError(true);
      hasError = true;
    } else {
      setStaffError(false);
    }

    if (!allowCreateWithoutProject && !selectedProject) {
      setProjectError(true);
      hasError = true;
    } else {
      setProjectError(false);
    }

    if (!selectedTechnique?.id) {
      setTechniqueError(true);
      hasError = true;
    } else {
      setTechniqueError(false);
    }

    if (!selectedInstrument?.id) {
      setInstrumentError(true);
      hasError = true;
    } else {
      setInstrumentError(false);
    }

    const nonEmptyRows = substrateRows.filter(
      (r) => (r.mode === 'existing' && r.existingSample) || (r.mode === 'new' && r.materialIds.length > 0)
    );
    const invalidNewRows = substrateRows.filter((r) => r.mode === 'new' && r.materialIds.length === 0);
    if (nonEmptyRows.length === 0 || invalidNewRows.length > 0) {
      setSubstrateError(true);
      hasError = true;
    } else {
      setSubstrateError(false);
    }

    if (!selectedDate) {
      showSnackbar('Please select a date.', 'error');
      hasError = true;
    }

    if (hasError) {
      if (!hasUnsavedChanges) {
        markAsInteracted();
      }
      showSnackbar(
        !hasCreateSampleForOthers && effectiveStaff && String(effectiveStaff.key) !== String(currentUser?.id)
          ? 'You can only create samples for yourself.'
          : 'Please fill out all required fields.',
        'error'
      );
      return;
    }

    const projectIdsForPolicy = selectedProject ? [selectedProject.id] : [];
    const multErr = validateProjectIdsForSampleMultiplicity(multiplicityPolicy, projectIdsForPolicy, resolveProjectDto);
    if (multErr) {
      showSnackbar(multErr, 'error');
      return;
    }

    const recipePayload = await buildCreateRecipePayloadAsync(backend, selectedFavorite, selectedRecipeTemplate);
    if (selectedFavorite && !recipePayload.recipe) {
      showSnackbar('Could not load recipe from favorite. Try again or pick a template only.', 'error');
      return;
    }

    const request: CreateMultiSampleGrowthRequest = {
      date: selectedDate!,
      ...(selectedProject?.id != null && { projectId: selectedProject.id }),
      staffKey: nullIfBlank(String(effectiveStaff!.key ?? effectiveStaff!.value ?? ''))!,
      techniqueId: nullIfBlank(selectedTechnique!.id)!,
      instrumentId: nullIfBlank(selectedInstrument!.id)!,
      label: growthLabel || undefined,
      description: description || undefined,
      reportingCategoryCode: selectedReportingCategory?.code ?? undefined,
      recipeTemplate: recipePayload.recipeTemplate,
      recipe: recipePayload.recipe,
      runtime: recipePayload.runtime,
      substrates: nonEmptyRows.map((r): CreateMultiSampleGrowthSubstrateDto =>
        r.mode === 'existing' && r.existingSample
          ? { existingSampleId: r.existingSample.id ?? undefined }
          : {
              materialIds: Array.from(new Set(r.materialIds.filter((id): id is number => id != null))),
              substrateVendor: r.vendor || undefined,
              substrateLot: r.lot || undefined,
              substrateOrientation: r.orientation || undefined,
              sampleHolder: r.holder || undefined,
              thickness: r.thickness || undefined,
              size: r.size || undefined,
            }
      ),
    };

    setSubmitting(true);
    try {
      const createdGrowthRun = await backend.sample.createMultiSampleGrowth(request);
      onSubmit(createdGrowthRun);

      showSnackbar('Multi-sample growth added.', 'success');
      resetStateAndClose();
    } catch (error) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create multi-sample growth.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <StandardDialog
      open={open}
      onClose={resetStateAndClose}
      title="Multi-Sample Growth"
      maxWidth="lg"
      onSubmit={handleSubmit}
      submitText="Add Growth Run"
      helpDocKey="dialog.multiSampleGrowth"
      isLoading={submitting}
      submitDisabled={submitting}
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes to this multi-sample growth. Are you sure you want to discard them?"
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Row 1: Project, Researcher, Date */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 1.25fr) minmax(240px, 1fr) 190px' },
            gap: 1.5,
            mb: 1.5,
            alignItems: 'start',
          }}
        >
          <EntityAutocompleteWithLookup
            label={`Project${allowCreateWithoutProject ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
            options={projectOptions}
            value={selectedProject}
            onChange={(newValue) => {
              setSelectedProject(newValue as { id: number; label: string } | null);
              if (newValue != null) setProjectError(false);
              markAsInteracted();
            }}
            onLookupClick={handleProjectLookup}
            multiple={false}
            placeholder={allowCreateWithoutProject ? 'No project' : undefined}
            error={projectError}
            helperText={projectError ? 'Project is required.' : (multiplicityHint ?? undefined)}
          />
          <FormAutocomplete
            options={staffOptions}
            getOptionLabel={(option) => option.value || ''}
            value={effectiveStaff}
            onChange={(_, newValue) => {
              setSelectedStaff(newValue);
              if (newValue?.key) setStaffError(false);
              markAsInteracted();
            }}
            label="Researcher"
            required
            error={staffError}
            helperText={
              staffError
                ? 'Researcher is required.'
                : currentUserNotInStaffList
                  ? 'Current user is not in the researcher list; contact an administrator.'
                  : !hasCreateSampleForOthers
                    ? 'Only yourself can be selected.'
                    : undefined
            }
            size="small"
          />
          <Box sx={{ width: { xs: '100%', md: 190 } }}>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
              size="small"
            />
          </Box>
        </Box>

        {/* Row 2: Technique, Instrument, Growth label */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(240px, 1fr) minmax(240px, 1fr) minmax(280px, 1.2fr)',
            },
            gap: 1.5,
            mb: 1.5,
            alignItems: 'start',
          }}
        >
          <FormAutocomplete
            options={techniqueOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={
              selectedTechnique?.id
                ? (techniqueOptions.find((t) => t.id === selectedTechnique!.id) ?? selectedTechnique)
                : null
            }
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setSelectedTechnique({ id: '', name: newValue });
              } else {
                setSelectedTechnique(newValue);
              }
              setSelectedInstrument(null);
              setSelectedFavorite(null);
              if (newValue) setTechniqueError(false);
              markAsInteracted();
            }}
            label="Growth Technique"
            required
            error={techniqueError}
            helperText={techniqueError ? 'Technique is required.' : undefined}
            size="small"
          />
          <FormAutocomplete
            options={instrumentOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            value={selectedInstrument}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setSelectedInstrument({ id: '', name: newValue });
              } else {
                setSelectedInstrument(newValue);
              }
              setSelectedFavorite(null);
              if (newValue) setInstrumentError(false);
              markAsInteracted();
            }}
            label="Instrument"
            required
            error={instrumentError}
            helperText={instrumentError ? 'Instrument is required.' : undefined}
            size="small"
          />
          <FormTextField
            label="Growth Run Label"
            value={growthLabel}
            onChange={(e) => {
              setGrowthLabel(e.target.value);
              markAsInteracted();
            }}
            size="small"
            fullWidth
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            mb: 1.5,
            alignItems: 'start',
          }}
        >
          <FormAutocomplete
            options={availableTemplates}
            getOptionLabel={(option) => String(option)}
            value={selectedRecipeTemplate}
            inputValue={selectedRecipeTemplate ?? ''}
            onChange={(_, newValue) => {
              setSelectedRecipeTemplate(typeof newValue === 'string' ? newValue : newValue);
              setSelectedFavorite(null);
              markAsInteracted();
            }}
            label="Recipe Template"
            disabled={!selectedTechnique?.id || !selectedInstrument?.id || loadingTemplates}
            helperText={
              loadingTemplates
                ? 'Loading templates...'
                : !selectedInstrument?.id
                  ? 'Select technique and instrument first'
                  : !availableTemplates.length
                    ? 'No recipe templates available'
                    : undefined
            }
            size="small"
          />
          <FormAutocomplete
            options={availableFavorites}
            getOptionLabel={(option) => option.name || ''}
            value={selectedFavorite}
            onChange={(_, newValue) => {
              setSelectedFavorite(newValue);
              if (newValue?.template) {
                setSelectedRecipeTemplate(newValue.template);
              } else if (newValue?.recipe?.templateName) {
                setSelectedRecipeTemplate(newValue.recipe.templateName);
              }
              markAsInteracted();
            }}
            label="Recipe Favorite"
            disabled={!selectedRecipeTemplate || loadingFavorites}
            helperText={
              loadingFavorites
                ? 'Loading favorites...'
                : !selectedRecipeTemplate
                  ? 'Select a recipe template first'
                  : availableFavorites.length === 0
                    ? 'No favorites for this template'
                    : 'Optional — pre-fill growth SYN recipe from a favorite'
            }
            size="small"
          />
        </Box>

        {/* Row 3: Reporting category + description */}
        <Box display="flex" gap={1.5} mb={1.5}>
          <FormAutocomplete
            options={reportingCategoryOptions}
            getOptionLabel={(option) => option.label || option.code || ''}
            value={selectedReportingCategory}
            onChange={(_, newValue) => {
              setSelectedReportingCategory(newValue);
              markAsInteracted();
            }}
            label="Reporting Category"
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
          />
        </Box>
        <Box mb={1.5}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Growth Description (optional)
          </Typography>
          <RichTextEditor
            value={description}
            onChange={(value) => {
              setDescription(value);
              markAsInteracted();
            }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Substrate rows */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
          <Typography variant="subtitle1" fontWeight={600}>
            Samples in run
          </Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddRow}>
            Add sample
          </Button>
        </Box>
        {substrateError ? (
          <Typography variant="body2" color="error" sx={{ mb: 0.75 }}>
            At least one substrate is required. Each row must either pick a sample from the pool or provide Material
            (required for Enter new).
          </Typography>
        ) : null}
        {substrateRows.map((row) => (
          <Box
            key={row.id}
            display="flex"
            alignItems="center"
            gap={1.5}
            mb={0.75}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.75 }}
          >
            <ToggleButtonGroup
              value={row.mode}
              exclusive
              onChange={(_, value) => value && handleRowModeChange(row.id, value)}
              size="small"
              sx={{
                height: 40,
                '& .MuiToggleButton-root': { height: 40, px: 1.25, textTransform: 'none' },
              }}
            >
              <ToggleButton value="existing" aria-label="Pick from pool">
                <ScienceIcon fontSize="small" sx={{ mr: 0.5 }} />
                From pool
              </ToggleButton>
              <ToggleButton value="new" aria-label="New substrate">
                <InventoryIcon fontSize="small" sx={{ mr: 0.5 }} />
                Enter new
              </ToggleButton>
            </ToggleButtonGroup>

            {row.mode === 'existing' ? (
              <>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 40 }}>
                  <Typography
                    variant="body2"
                    color={row.existingSample ? 'text.primary' : 'text.secondary'}
                    sx={{ fontStyle: row.existingSample ? undefined : 'italic' }}
                  >
                    {row.existingSample?.sampleLabel ?? 'No sample selected'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleRowPickSample(row.id)}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  Pick sample
                </Button>
              </>
            ) : (
              <Box display="flex" flexDirection="column" gap={1} flex={1} minWidth={0}>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <FormAutocomplete
                    options={materialOptions}
                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
                    filterOptions={(opts, params) =>
                      filterAutocompleteOptionsByMatchRank(opts, params, (o) =>
                        typeof o === 'string' ? o : String((o as IdLabelPair).label ?? '')
                      )
                    }
                    isOptionEqualToValue={(opt, val) => (opt as IdLabelPair).id === (val as IdLabelPair)?.id}
                    value={
                      row.materialIds[0] != null
                        ? (materialOptions.find((m) => m.id === row.materialIds[0]) ?? null)
                        : null
                    }
                    onChange={(_, newValue) => {
                      const ids = newValue ? [(newValue as IdLabelPair).id] : [];
                      handleRowNewSubstrateChange(row.id, 'materialIds', ids);
                    }}
                    label="Material"
                    required
                    size="small"
                    sx={{ minWidth: 140, flex: 1 }}
                  />
                  <FormAutocomplete
                    freeSolo
                    options={substrateVendorOptions}
                    value={row.vendor}
                    onChange={(_, v) => handleRowNewSubstrateChange(row.id, 'vendor', (v ?? '') as string)}
                    onInputChange={(_, v) => handleRowNewSubstrateChange(row.id, 'vendor', v)}
                    label="Vendor"
                    size="small"
                    sx={{ minWidth: 100, flex: 0.8 }}
                  />
                  <FormAutocomplete
                    freeSolo
                    options={substrateLotOptions}
                    value={row.lot}
                    onChange={(_, v) => handleRowNewSubstrateChange(row.id, 'lot', (v ?? '') as string)}
                    onInputChange={(_, v) => handleRowNewSubstrateChange(row.id, 'lot', v)}
                    label="Lot"
                    size="small"
                    sx={{ minWidth: 100, flex: 0.8 }}
                  />
                  <FormAutocomplete
                    freeSolo
                    options={substrateOrientationOptions}
                    value={row.orientation}
                    onChange={(_, v) => handleRowNewSubstrateChange(row.id, 'orientation', (v ?? '') as string)}
                    onInputChange={(_, v) => handleRowNewSubstrateChange(row.id, 'orientation', v)}
                    label="Orientation"
                    size="small"
                    sx={{ minWidth: 100, flex: 0.8 }}
                  />
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <FormAutocomplete
                    freeSolo
                    options={(options.sampleDropdownChoices?.sampleHolders ?? []).filter(Boolean)}
                    value={row.holder}
                    onChange={(_, v) => handleRowNewSubstrateChange(row.id, 'holder', (v ?? '') as string)}
                    onInputChange={(_, v) => handleRowNewSubstrateChange(row.id, 'holder', v)}
                    label="Holder"
                    size="small"
                    sx={{ minWidth: 90, flex: 0.7 }}
                  />
                  <FormAutocomplete
                    freeSolo
                    options={(options.sampleDropdownChoices?.thicknesses ?? []).filter(Boolean)}
                    value={row.thickness}
                    onChange={(_, v) => handleRowNewSubstrateChange(row.id, 'thickness', (v ?? '') as string)}
                    onInputChange={(_, v) => handleRowNewSubstrateChange(row.id, 'thickness', v)}
                    label="Thickness"
                    size="small"
                    sx={{ minWidth: 90, flex: 0.7 }}
                  />
                  <FormAutocomplete
                    freeSolo
                    options={(options.sampleDropdownChoices?.sizes ?? []).filter(Boolean)}
                    value={row.size}
                    onChange={(_, v) => handleRowNewSubstrateChange(row.id, 'size', (v ?? '') as string)}
                    onInputChange={(_, v) => handleRowNewSubstrateChange(row.id, 'size', v)}
                    label="Size"
                    size="small"
                    sx={{ minWidth: 90, flex: 0.7 }}
                  />
                </Box>
              </Box>
            )}

            <IconButton
              aria-label="Remove substrate"
              onClick={() => handleRemoveRow(row.id)}
              size="small"
              disabled={substrateRows.length === 1}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </LocalizationProvider>
    </StandardDialog>
  );
};

export default MultiSampleGrowthDialog;
